import React, { useMemo } from 'react';

import SearchableSelect, {
    SearchableSelectType,
} from 'components/inputs/searchableSelect';
import styled from 'styled-components';

import aampeTheme from '../../../../../constants/aampeTheme';
import ICONS from '../../../../../constants/icons';

import useComponentTypesQuery from 'hooks/component-types/useComponentTypesQuery';
import useLabelsQuery from 'hooks/labels/useLabelsQuery';

import { ComponentTypeMap } from 'models/component-types-model';

import { SelectedCellLabels } from '../../contants';

const customSelectStyles = {
    control: (base: any) => ({
        ...base,
        border: 'none',
        backgroundColor: 'transparent',
        boxShadow: 'none',
        cursor: 'pointer',
        padding: 0,
        minHeight: 'auto',
    }),
    valueContainer: (base: any) => ({
        ...base,
        padding: 0,
    }),

    singleValue: (base: any) => ({
        ...base,
        color: aampeTheme.colors.neutral09,
        fontSize: aampeTheme.size.font.heading,
        fontWeight: aampeTheme.weights.light,
    }),

    dropdownIndicator: (base: any) => ({
        ...base,
        padding: 0,
        svg: {
            width: 18,
            height: 18,
            color: aampeTheme.colors.textAccessibleWG01,
        },
    }),
    indicatorsContainer: () => ({
        display: 'none',
    }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
    menu: (base: any) => ({
        ...base,
        borderRadius: aampeTheme.borderRadius.default,
        overflow: 'hidden',
        zIndex: 10,
    }),
    option: (base: any, state: any) => ({
        ...base,
        backgroundColor: state.isFocused
            ? aampeTheme.colors.interactiveWG01
            : aampeTheme.colors.backgroundWG01,
        color: aampeTheme.colors.neutral09,
        cursor: 'pointer',
    }),
};

interface DrawerHeaderProps {
    onClose: () => void;
    xAxisComponent: string;
    yAxisComponent: string;
    setSelectedCell: (value: SelectedCellLabels) => void;
    selectedCellLabels: SelectedCellLabels;
}

const DrawerHeader: React.FC<DrawerHeaderProps> = ({
    onClose,
    xAxisComponent,
    yAxisComponent,
    setSelectedCell,
    selectedCellLabels,
}) => {
    const { data: componentTypes } = useComponentTypesQuery();
    const componentTypesMap = useMemo<ComponentTypeMap>(
        () => componentTypes?.mapById || {},
        [componentTypes],
    );
    const { data: xAxislabels } = useLabelsQuery(xAxisComponent);
    const { data: yAxislabels } = useLabelsQuery(yAxisComponent);

    const xLabels = useMemo<SearchableSelectType[]>(() => {
        return (
            xAxislabels?.map((label) => ({
                label: label.label_name,
                value: label.id,
            })) ?? []
        );
    }, [xAxislabels]);

    const yLabels = useMemo<SearchableSelectType[]>(() => {
        return (
            yAxislabels?.map((label) => ({
                label: label.label_name,
                value: label.id,
            })) ?? []
        );
    }, [yAxislabels]);

    return (
        <Container>
            <ComponentSection>
                <ComponentContainer>
                    <ComponentName>
                        {componentTypesMap[xAxisComponent].name}
                    </ComponentName>
                    <SearchableSelect
                        customStyles={customSelectStyles}
                        name={''}
                        value={selectedCellLabels.xAxisSelectedLabel}
                        options={xLabels}
                        onChange={(_, value) => {
                            setSelectedCell({
                                ...selectedCellLabels,
                                xAxisSelectedLabel:
                                    value as SearchableSelectType,
                            });
                        }}
                    />
                </ComponentContainer>
                <ICONS.simpleSuffleLight
                    color={aampeTheme.colors.textAccessibleWG01}
                />
                <ComponentContainer>
                    <ComponentName>
                        {componentTypesMap[yAxisComponent].name}
                    </ComponentName>
                    <SearchableSelect
                        name={''}
                        value={selectedCellLabels.yAxisSelectedLabel}
                        options={yLabels}
                        onChange={(_, value) => {
                            setSelectedCell({
                                ...selectedCellLabels,
                                yAxisSelectedLabel:
                                    value as SearchableSelectType,
                            });
                        }}
                        customStyles={customSelectStyles}
                    />
                </ComponentContainer>
            </ComponentSection>
            <CloseIcon onClick={onClose} />
        </Container>
    );
};

const Container = styled.span(() => ({
    display: 'flex',
    backgroundColor: aampeTheme.colors.backgroundWG02,
    padding: aampeTheme.spacing4(5),
    borderBottom: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    justifyContent: 'space-between',
}));

const ComponentName = styled.span({
    fontSize: aampeTheme.size.font.textSmall,
    fontWeight: aampeTheme.weights.medium,
    color: aampeTheme.colors.textAccessibleWG01,
});

const ComponentContainer = styled.div({
    display: 'flex',
    borderRadius: aampeTheme.borderRadius.default,
    padding: aampeTheme.spacing4(2, 3),
    flexDirection: 'column',
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    gap: aampeTheme.spacing4(1),
});

const ComponentSection = styled.div({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: aampeTheme.spacing4(4),
});

const CloseIcon = styled(ICONS.close)({
    color: aampeTheme.colors.neutral09,
    cursor: 'pointer',
});

export default DrawerHeader;
